import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Snackbar, Alert } from '@mui/material';
import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import NewInput from '../../Components/NewInput/NewInput';
import MapPicker from '../../Components/MapPicker/MapPicker';
import ColorPicker from '../../Components/ColorPicker/ColorPicker';
import { settingsAPI } from '../../services/apiService';
import './Settings.css';

function Settings() {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState({
        siteTitle: '',
        siteDescription: '',
        tax: '',
        email: '',
        phone: '',
        address: '',
        logo: null,
        headerImage: null,
        homeImage: null,
        instagram: '',
        telegram: '',
        whatsapp: '',
        bale: '',
        work_time: '',
        first_color: '#EADBC8',
        second_color: '#DAC0A3',
        main_color: '#32453A',
        invoice_button_color: '#A67C52',
        button_text_color: '#000000',
        button_light_text_color: '#FFFFFF',
    });

    const localizeDigits = (value) => {
        const locale = i18n.language || 'en';
        const digits = Array.from({ length: 10 }, (_, index) =>
            new Intl.NumberFormat(locale, { useGrouping: false }).format(index)
        );

        return String(value ?? '').replace(/[0-9]/g, (digit) => digits[Number(digit)]);
    };

    const toEnglishDigits = (value = '') =>
        value
            .toString()
            .replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d))
            .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d));

    const toPersianDigits = (value = '') => value.toString().replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);

    const formatInputDigits = (value) => (i18n.language === 'fa' ? toPersianDigits(value) : value);

    const parseLatLonFromUrl = (url) => {
        if (!url) return null;
        const match = url.match(/mlat=([-0-9.]+)&mlon=([-0-9.]+)/);
        if (match) {
            return { lat: parseFloat(match[1]), lng: parseFloat(match[2]) };
        }
        return null;
    };

    const [imagePreview, setImagePreview] = useState({
        logo: '',
        headerImage: '',
        homeImage: '',
    });

    const [mapIframeSrc, setMapIframeSrc] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await settingsAPI.getSettings();

                setFormData({
                    siteTitle: data.brand_name || '',
                    siteDescription: data.description || '',
                    tax: data.tax || '',
                    email: data.email || '',
                    phone: data.phone_number || '',
                    instagram: data.instagram_page_id || '',
                    telegram: data.telegram_id || '',
                    whatsapp: data.whatsapp_number || '',
                    bale: data.bale_id || '',
                    address: data.address || '',
                    logo: data.logo || null,
                    headerImage: data.header_image || null,
                    homeImage: data.home_image || null,
                    work_time: data.work_time || '',
                    first_color: data.first_color || '#EADBC8',
                    second_color: data.second_color || '#DAC0A3',
                    main_color: data.main_color || '#32453A',
                    invoice_button_color: data.invoice_button_color || '#A67C52',
                    button_text_color: data.button_text_color || '#000000',
                    button_light_text_color: data.button_light_text_color || '#FFFFFF',
                });

                setImagePreview({
                    logo: data.logo || '',
                    headerImage: data.header_image || '',
                    homeImage: data.home_image || '',
                });

                setMapIframeSrc(data.location || '');
            } catch (err) {
                setSnackbar({
                    open: true,
                    message: err.message || t('fetch_error'),
                    severity: 'error',
                });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [t]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: name === 'tax' ? toEnglishDigits(value) : value,
        }));
    };

    const handleColorChange = (name, color) => {
        setFormData((prev) => ({
            ...prev,
            [name]: color,
        }));
    };

    const handleImageChange = (e) => {
        const { name, files } = e.target;
        if (files && files[0]) {
            const file = files[0];
            setFormData((prev) => ({ ...prev, [name]: file }));
            const previewURL = URL.createObjectURL(file);
            setImagePreview((prev) => ({ ...prev, [name]: previewURL }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const formDataToSend = new FormData();
        formDataToSend.append('brand_name', formData.siteTitle);
        formDataToSend.append('description', formData.siteDescription);
        formDataToSend.append('tax', formData.tax);
        formDataToSend.append('email', formData.email);
        formDataToSend.append('phone_number', formData.phone);
        formDataToSend.append('address', formData.address);
        formDataToSend.append('instagram_page_id', formData.instagram);
        formDataToSend.append('telegram_id', formData.telegram);
        formDataToSend.append('whatsapp_number', formData.whatsapp);
        formDataToSend.append('bale_id', formData.bale);
        formDataToSend.append('location', mapIframeSrc);
        formDataToSend.append('work_time', formData.work_time);
        formDataToSend.append('first_color', formData.first_color);
        formDataToSend.append('second_color', formData.second_color);
        formDataToSend.append('main_color', formData.main_color);
        formDataToSend.append('invoice_button_color', formData.invoice_button_color);
        formDataToSend.append('button_text_color', formData.button_text_color);
        formDataToSend.append('button_light_text_color', formData.button_light_text_color);

        if (formData.logo instanceof File) formDataToSend.append('logo', formData.logo);
        if (formData.headerImage instanceof File)
            formDataToSend.append('header_image', formData.headerImage);
        if (formData.homeImage instanceof File)
            formDataToSend.append('home_image', formData.homeImage);

        try {
            await settingsAPI.updateSettings(formDataToSend);

            setSnackbar({
                open: true,
                message: t('save_success'),
                severity: 'success',
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.message || t('save_error'),
                severity: 'error',
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const getImageLabel = (name) => {
        switch (name) {
            case 'logo':
                return t('logo');
            case 'headerImage':
                return t('header_image');
            case 'homeImage':
                return t('home_image');
            default:
                return '';
        }
    };
    return (
        <div className="settings_page">
            <Sidebar />
            <div className="settings_main">
                <Navbar />
                <div className="settings_content">
                    <div className="settings_card">
                        <form className="settings_card_form" onSubmit={handleSubmit}>
                            <div>
                                <div className="settings_section">
                                    <h3>{t('general_info')}</h3>
                                    <NewInput
                                        label={t('site_title')}
                                        name="siteTitle"
                                        type="text"
                                        value={formData.siteTitle}
                                        onChange={handleChange}
                                        required
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('site_description')}
                                        name="siteDescription"
                                        type="text"
                                        value={formData.siteDescription}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('tax')}
                                        name="tax"
                                        type="text"
                                        value={formatInputDigits(formData.tax)}
                                        onChange={(e) => {
                                            const rawValue = toEnglishDigits(e.target.value);
                                            handleChange({
                                                target: { name: 'tax', value: rawValue },
                                            });
                                        }}
                                        className="setting_input"
                                    />
                                </div>
                                <div className="settings_section">
                                    <h3>{t('contact_info')}</h3>
                                    <NewInput
                                        label={t('email')}
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('phone')}
                                        name="phone"
                                        type="text"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('address')}
                                        name="address"
                                        type="text"
                                        value={formData.address}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('work_time')}
                                        name="work_time"
                                        type="text"
                                        value={formData.work_time}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('instagram')}
                                        name="instagram"
                                        type="text"
                                        value={formData.instagram}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('telegram')}
                                        name="telegram"
                                        type="text"
                                        value={formData.telegram}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('whatsapp')}
                                        name="whatsapp"
                                        type="text"
                                        value={formData.whatsapp}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                    <NewInput
                                        label={t('bale')}
                                        name="bale"
                                        type="text"
                                        value={formData.bale}
                                        onChange={handleChange}
                                        className="setting_input"
                                    />
                                </div>
                            </div>

                            <div>
                                <div className="settings_section">
                                    <h3>{t('site_images')}</h3>
                                    <div className="image_section">
                                        {['logo', 'headerImage', 'homeImage'].map((name) => (
                                            <div className="image_input" key={name}>
                                                <label htmlFor={name}>
                                                    <p>{getImageLabel(name)}</p>
                                                    {imagePreview[name] && (
                                                        <img
                                                            src={imagePreview[name]}
                                                            alt={name}
                                                            className="preview_img"
                                                        />
                                                    )}
                                                    <input
                                                        id={name}
                                                        type="file"
                                                        name={name}
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <h3>{t('theme_colors')}</h3>
                                <div className="settings_color_section">
                                    <ColorPicker
                                        name="first_color"
                                        label={t('first_color')}
                                        value={formData.first_color}
                                        onChange={(color) =>
                                            handleColorChange('first_color', color)
                                        }
                                    />

                                    <ColorPicker
                                        name="second_color"
                                        label={t('second_color')}
                                        value={formData.second_color}
                                        onChange={(color) =>
                                            handleColorChange('second_color', color)
                                        }
                                    />
                                    <ColorPicker
                                        name="main_color"
                                        label={t('main_color')}
                                        value={formData.main_color}
                                        onChange={(color) => handleColorChange('main_color', color)}
                                    />

                                    <ColorPicker
                                        name="invoice_button_color"
                                        label={t('invoice_button_color')}
                                        value={formData.invoice_button_color}
                                        onChange={(color) =>
                                            handleColorChange('invoice_button_color', color)
                                        }
                                    />
                                    <ColorPicker
                                        name="button_text_color"
                                        label={t('button_text_color')}
                                        value={formData.button_text_color}
                                        onChange={(color) =>
                                            handleColorChange('button_text_color', color)
                                        }
                                    />
                                    <ColorPicker
                                        name="invoice_light_text_color"
                                        label={t('button_light_text_color')}
                                        value={formData.button_light_text_color}
                                        onChange={(color) =>
                                            handleColorChange('button_light_text_color', color)
                                        }
                                    />
                                </div>
                                <div className="settings_section">
                                    <h3>{t('map_location')}</h3>
                                    <MapPicker
                                        onLocationSelect={setMapIframeSrc}
                                        initialPositionUrl={mapIframeSrc}
                                    />
                                </div>
                            </div>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={saving}
                                className="save_btn"
                            >
                                {saving ? t('saving') : t('save_settings')}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default Settings;
